import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import CheckBox from 'rc-checkbox';
import DatePicker from 'react-datepicker';
import SubmitButton from '../common/SubmitButton.jsx';
import { runTicketAuditFields } from '../../config/form-fields.js';
import ErrorMessage from '../common/ErrorMessage.jsx';
import LoadingSpinner from '../common/LoadingSpinner.jsx';
import useUsers from '../../hooks/useUsers.js';
import {
    timePeriodOptions,
    userRoleOptions
} from '../../config/dropdown-options.js';
import useReporting from '../../hooks/useReporting.js';
import runTicketAuditValidation from '../../validation/run-ticket-audit-validation.js';
import TicketCard from './TicketCard.jsx';
import callApi from '../../utils/call-api.js';
import analyzeApiResponse from '../../utils/analyze-api-response.js';
import callOysterApi from '../../utils/call-oyster-api.js';

const selectStyles = {
    control: (base) => ({
        ...base,
        minHeight: 38,
        borderRadius: 8,
        borderColor: '#d1d5db',
        fontSize: '0.875rem'
    }),
    menu: (base) => ({
        ...base,
        borderRadius: 8,
        overflow: 'hidden'
    })
};

const auditStatusOptions = [
    { value: 'all', label: 'All (full list, pass & fail)' },
    { value: 'pass', label: 'Pass only' },
    { value: 'fail', label: 'Fail only' }
];

/**
 * AuditTicketsForm — weekly audit (OR-279): teams, techs, filters, lead sign-off, management view.
 *
 * @component
 * @param {{ isLeadership?: boolean, isTeamLead?: boolean, hdTeam?: string }} props
 * @returns {JSX.Element}
 */
const AuditTicketsForm = ({
    isLeadership = false,
    isTeamLead = false,
    hdTeam: leadHdTeam = ''
}) => {
    const [allUserOptions, setAllUserOptions] = useState([]);
    const [techOptions, setTechOptions] = useState(null);
    const [teamSelectOptions, setTeamSelectOptions] = useState([
        { value: '', label: 'All teams (search any technician)' }
    ]);
    const [showCustomTime, setShowCustomTime] = useState(false);
    const [signInitials, setSignInitials] = useState('');
    const [signFeedback, setSignFeedback] = useState('');
    const [signLeadDate, setSignLeadDate] = useState(() =>
        new Date().toISOString().slice(0, 10)
    );
    const [signLoading, setSignLoading] = useState(false);
    const [signMessage, setSignMessage] = useState(null);
    const [mgmtMonth, setMgmtMonth] = useState(() =>
        new Date().toISOString().slice(0, 7)
    );
    const [mgmtData, setMgmtData] = useState(null);
    const [mgmtLoading, setMgmtLoading] = useState(false);
    const [techReviewFlags, setTechReviewFlags] = useState({});
    /** Shown after a successful API save (cleared when you run Start audit again). */
    const [signoffSavedSummary, setSignoffSavedSummary] = useState(null);
    /** Lead sign-offs for the current reporting period (`null` = not loaded yet). */
    const [periodSignoffs, setPeriodSignoffs] = useState(null);
    /** `period` from `/signoffs/for-period` (matches time filters). */
    const [periodSignoffsMeta, setPeriodSignoffsMeta] = useState(null);
    const [periodSignoffsLoading, setPeriodSignoffsLoading] = useState(false);

    const { error: userError, loading: userLoading, searchUsers } = useUsers();
    const teamSelectLocked = !!(isTeamLead && !isLeadership);
    const leadTeamNormalized =
        leadHdTeam != null ? String(leadHdTeam).trim() : '';

    const {
        error: reportingError,
        loading: reportingLoading,
        tickets,
        auditPeriod,
        auditContext,
        publicScore,
        getWeeklyAuditTickets,
        getWeeklyAuditTeams,
        getWeeklyAuditTechsForTeam,
        getWeeklyAuditManagementSummary,
        reset
    } = useReporting();

    const handleClearAuditResults = () => {
        setSignoffSavedSummary(null);
        setSignMessage(null);
        setTechReviewFlags({});
        reset();
    };

    const formik = useFormik({
        initialValues: runTicketAuditFields,
        validationSchema: runTicketAuditValidation,
        onSubmit: async (values) => {
            const modifiedValues = { ...values };

            Object.keys(modifiedValues).forEach((key) => {
                if (
                    modifiedValues[key] === null ||
                    (Array.isArray(modifiedValues[key]) &&
                        modifiedValues[key].length === 0)
                ) {
                    delete modifiedValues[key];
                }
            });

            if (modifiedValues.teamHdName === '') {
                delete modifiedValues.teamHdName;
            }
            if (modifiedValues.userId === '') {
                delete modifiedValues.userId;
            }
            if (
                modifiedValues.ticketHumanId === '' ||
                modifiedValues.ticketHumanId == null
            ) {
                delete modifiedValues.ticketHumanId;
            }

            setSignMessage(null);
            setSignoffSavedSummary(null);
            await getWeeklyAuditTickets(modifiedValues);
        }
    });

    const userOptionsForSelect = useMemo(() => {
        if (formik.values.teamHdName && techOptions) {
            return [
                { value: '', label: 'All technicians (this team)' },
                ...techOptions.map((t) => ({
                    value: t.oracleUserId,
                    label: t.label
                }))
            ];
        }
        if (isLeadership) {
            return allUserOptions;
        }
        return [];
    }, [formik.values.teamHdName, techOptions, allUserOptions, isLeadership]);

    const applySignoffRowToForm = useCallback((row) => {
        if (!row) return;
        setSignInitials(row.initials || '');
        setSignFeedback(row.feedbackNotes || '');
        if (row.leadValidatedAt) {
            const d = new Date(row.leadValidatedAt);
            if (!Number.isNaN(d.getTime())) {
                setSignLeadDate(d.toISOString().slice(0, 10));
            }
        }
    }, []);

    const clearSignoffFormFields = useCallback(() => {
        setSignInitials('');
        setSignFeedback('');
        setSignLeadDate(new Date().toISOString().slice(0, 10));
    }, []);

    const fetchPeriodSignoffs = useCallback(async () => {
        setPeriodSignoffsLoading(true);
        try {
            const params = new URLSearchParams();
            params.set('timePeriod', formik.values.timePeriod || 'thisWeek');
            if (
                auditPeriod?.startDate != null &&
                auditPeriod?.endDate != null
            ) {
                params.set(
                    'weekStart',
                    new Date(auditPeriod.startDate).toISOString()
                );
                params.set(
                    'weekEnd',
                    new Date(auditPeriod.endDate).toISOString()
                );
            } else if (formik.values.timePeriod === 'custom') {
                if (
                    formik.values.customStartDate instanceof Date &&
                    !Number.isNaN(formik.values.customStartDate.getTime())
                ) {
                    params.set(
                        'customStartDate',
                        formik.values.customStartDate.toISOString()
                    );
                }
                if (
                    formik.values.customEndDate instanceof Date &&
                    !Number.isNaN(formik.values.customEndDate.getTime())
                ) {
                    params.set(
                        'customEndDate',
                        formik.values.customEndDate.toISOString()
                    );
                }
            }
            const res = await callApi(
                `/reporting/weekly-audit/signoffs/for-period?${params.toString()}`,
                'get'
            );
            if (!res.ok) {
                setPeriodSignoffs([]);
                setPeriodSignoffsMeta(null);
                return;
            }
            const data = await res.json();
            setPeriodSignoffs(data.signoffs || []);
            setPeriodSignoffsMeta(data.period || null);
        } catch {
            setPeriodSignoffs([]);
            setPeriodSignoffsMeta(null);
        } finally {
            setPeriodSignoffsLoading(false);
        }
    }, [
        formik.values.timePeriod,
        formik.values.customStartDate,
        formik.values.customEndDate,
        auditPeriod?.startDate,
        auditPeriod?.endDate
    ]);

    useEffect(() => {
        fetchPeriodSignoffs();
    }, [fetchPeriodSignoffs]);

    const handleTechnicianChange = (selectedOption) => {
        const v = selectedOption ? selectedOption.value : '';
        formik.setFieldValue('userId', v);
        if (!v) return;
        if (periodSignoffs === null) return;
        const row = periodSignoffs.find(
            (s) => String(s.techOracleUserId) === String(v)
        );
        if (row) applySignoffRowToForm(row);
        else clearSignoffFormFields();
    };

    useEffect(() => {
        if (!formik.values.userId) {
            clearSignoffFormFields();
        }
    }, [formik.values.userId, clearSignoffFormFields]);

    useEffect(() => {
        if (periodSignoffs === null || !formik.values.userId) return;
        const row = periodSignoffs.find(
            (s) => String(s.techOracleUserId) === String(formik.values.userId)
        );
        if (row) applySignoffRowToForm(row);
    }, [periodSignoffs, formik.values.userId, applySignoffRowToForm]);

    const buildTeamSelectOptions = (teamNames) => {
        const merged = [
            ...new Set([
                ...teamNames.filter((n) => typeof n === 'string' && n.trim()),
                'Leadership'
            ])
        ].sort((a, b) => a.localeCompare(b));
        return [
            { value: '', label: 'All teams (search any technician)' },
            ...merged.map((t) => ({ value: t, label: t }))
        ];
    };

    /**
     * 1) `/reporting/weekly-audit/teams` — Oracle reads Pearl (`teams` + `users.hdTeam`);
     *    works without Oyster running (local dev). 2–3) Oyster proxy fallbacks
     *    same as ManageTeamsModal / TeamTasksPage when extra parity is needed.
     */
    const loadTeams = useCallback(async () => {
        if (teamSelectLocked) {
            if (leadTeamNormalized) {
                setTeamSelectOptions([
                    { value: leadTeamNormalized, label: leadTeamNormalized }
                ]);
            } else {
                setTeamSelectOptions([]);
            }
            return;
        }

        try {
            const data = await getWeeklyAuditTeams();
            if (data && Array.isArray(data.teams)) {
                setTeamSelectOptions(buildTeamSelectOptions(data.teams));
                return;
            }
        } catch {
            // try Oyster fallbacks below
        }

        try {
            const response = await callOysterApi('/dashboard/teams', 'GET');
            if (response.ok) {
                const data = await response.json();
                const list = data?.payload?.teams;
                if (data.success && Array.isArray(list)) {
                    setTeamSelectOptions(buildTeamSelectOptions(list));
                    return;
                }
            }
        } catch {
            // try next fallback
        }

        try {
            const response = await callOysterApi('/teams', 'GET');
            if (response.ok) {
                const data = await response.json();
                if (data.success && Array.isArray(data.payload)) {
                    const names = data.payload
                        .map((t) => (t && t.name ? String(t.name).trim() : ''))
                        .filter(Boolean);
                    if (names.length) {
                        setTeamSelectOptions(buildTeamSelectOptions(names));
                        return;
                    }
                }
            }
        } catch {
            // try next fallback
        }

        try {
            const response = await callOysterApi(
                '/dashboard/team-users',
                'GET'
            );
            if (response.ok) {
                const data = await response.json();
                const rows = data.payload;
                if (Array.isArray(rows)) {
                    const names = [
                        ...new Set(
                            rows
                                .map((r) =>
                                    r && r.team ? String(r.team).trim() : ''
                                )
                                .filter((n) => n && n !== 'Unassigned')
                        )
                    ];
                    if (names.length) {
                        setTeamSelectOptions(buildTeamSelectOptions(names));
                    }
                }
            }
        } catch {
            // leave default empty option
        }
    }, [teamSelectLocked, leadTeamNormalized, getWeeklyAuditTeams]);

    useEffect(() => {
        loadTeams();
    }, [loadTeams]);

    useEffect(() => {
        if (!teamSelectLocked || !leadTeamNormalized) {
            return;
        }
        formik.setFieldValue('teamHdName', leadTeamNormalized);
        formik.setFieldValue('userId', '');
    }, [teamSelectLocked, leadTeamNormalized]);

    useEffect(() => {
        const run = async () => {
            if (!formik.values.teamHdName) {
                setTechOptions(null);
                return;
            }
            const data = await getWeeklyAuditTechsForTeam(
                formik.values.teamHdName
            );
            setTechOptions(data?.techs || []);
        };
        run();
    }, [formik.values.teamHdName, getWeeklyAuditTechsForTeam]);

    const loadManagementSummary = async () => {
        setMgmtLoading(true);
        setMgmtData(null);
        try {
            const data = await getWeeklyAuditManagementSummary(mgmtMonth);
            setMgmtData(data);
        } finally {
            setMgmtLoading(false);
        }
    };

    const submitSignoff = async () => {
        if (!auditPeriod || !tickets?.length) {
            setSignMessage('Run an audit first.');
            return;
        }
        if (!formik.values.userId) {
            setSignMessage(
                'Select a technician to sign off. Team-wide ticket lists need a specific tech for sign-off.'
            );
            return;
        }
        if (!signInitials.trim()) {
            setSignMessage('Initials are required.');
            return;
        }
        setSignLoading(true);
        setSignMessage(null);
        try {
            const reviewFlagTicketHumanIds = tickets
                .filter((t) => {
                    const rowKey = String(t.humanId || t._id || '').trim();
                    return !!rowKey && !!techReviewFlags[rowKey];
                })
                .map((t) => String(t.humanId || '').trim())
                .filter(Boolean);
            const res = await callApi(
                '/reporting/weekly-audit/signoffs',
                'post',
                {
                    techOracleUserId: formik.values.userId,
                    weekStart: auditPeriod.startDate,
                    weekEnd: auditPeriod.endDate,
                    initials: signInitials.trim(),
                    feedbackNotes: signFeedback,
                    auditTicketHumanIds: tickets.map((t) => t.humanId),
                    auditTicketPearlIds: tickets.map((t) =>
                        t._id != null ? String(t._id).trim() : ''
                    ),
                    reviewFlagTicketHumanIds,
                    teamHdName: formik.values.teamHdName || '',
                    leadValidatedAt: signLeadDate
                        ? new Date(signLeadDate).toISOString()
                        : new Date().toISOString(),
                    publicScoreClosedTotal: publicScore?.closedTotal ?? null,
                    publicScoreFailedPct: publicScore?.failedPct ?? null
                }
            );
            if (!res.ok) {
                const msg = await analyzeApiResponse(res);
                setSignMessage(msg || 'Sign-off failed');
            } else {
                const techLabel =
                    userOptionsForSelect.find(
                        (o) => o.value === formik.values.userId
                    )?.label || '';
                const periodStr =
                    auditPeriod?.startDate && auditPeriod?.endDate
                        ? `${new Date(
                              auditPeriod.startDate
                          ).toLocaleDateString()} – ${new Date(
                              auditPeriod.endDate
                          ).toLocaleDateString()}`
                        : '';
                const initialsDone = signInitials.trim();
                setSignoffSavedSummary({
                    techName: techLabel,
                    periodLabel: periodStr,
                    ticketCount: tickets.length,
                    reviewFlagCount: reviewFlagTicketHumanIds.length,
                    initials: initialsDone,
                    savedAt: Date.now()
                });
                setSignInitials('');
                setSignFeedback('');
                setSignLeadDate(new Date().toISOString().slice(0, 10));
                setTechReviewFlags({});
                setSignMessage(null);
                if (isLeadership) {
                    loadManagementSummary();
                }
                await fetchPeriodSignoffs();
            }
        } catch (e) {
            setSignMessage(e.message || 'Sign-off failed');
        } finally {
            setSignLoading(false);
        }
    };

    useEffect(() => {
        if (!isLeadership) {
            setAllUserOptions([]);
            return;
        }
        const getAllUsers = async () => {
            const roleIds = userRoleOptions.map((o) => o.value);
            const lists = await Promise.all(
                roleIds.map((roleId) =>
                    searchUsers('', 1, roleId, 500).then((r) => r || [])
                )
            );
            const byId = new Map();
            lists.flat().forEach((u) => {
                if (u?.id) byId.set(u.id, u);
            });
            let returnedUsers = Array.from(byId.values());

            returnedUsers.sort((a, b) =>
                a.firstName.localeCompare(b.firstName)
            );

            returnedUsers = returnedUsers.map((u) => ({
                value: u.id,
                label: `${u.firstName} ${u.lastName}`
            }));

            setAllUserOptions(returnedUsers);
        };

        getAllUsers();
    }, [isLeadership, searchUsers]);

    useEffect(() => {
        if (showCustomTime) {
            formik.setFieldValue('timePeriod', 'custom');
        } else {
            formik.setFieldValue('timePeriod', 'thisWeek');
        }
    }, [showCustomTime]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const onTechReviewFlagChange = (ticketKey, checked) => {
        setTechReviewFlags((prev) => {
            const next = { ...prev };
            if (checked) next[ticketKey] = true;
            else delete next[ticketKey];
            return next;
        });
    };

    const periodLabel =
        auditPeriod?.startDate && auditPeriod?.endDate
            ? `${new Date(
                  auditPeriod.startDate
              ).toLocaleDateString()} – ${new Date(
                  auditPeriod.endDate
              ).toLocaleDateString()}`
            : null;

    const hasRunWithNoTickets =
        Array.isArray(tickets) &&
        tickets.length === 0 &&
        !reportingError &&
        auditContext?.mode !== 'leadership';
    const showTicketResults = Array.isArray(tickets) && tickets.length > 0;
    const signMessageClass = signMessage
        ? 'audit-weekly-msg audit-weekly-msg--err'
        : 'audit-weekly-msg';

    const showTechOnCards = !!auditContext?.showTechColumn;

    const teamWideNoTechSelected =
        !!formik.values.teamHdName &&
        formik.values.teamHdName !== 'Leadership' &&
        !formik.values.userId;

    let ticketLimitHint;
    if (formik.values.auditStatus === 'all') {
        ticketLimitHint =
            'Ignored when Result is "All" — every closed ticket is listed.';
    } else if (teamWideNoTechSelected) {
        ticketLimitHint = 'Per technician when auditing a whole team.';
    } else {
        ticketLimitHint = 'Max tickets in the result set.';
    }

    const filterPeriodLabelDisplay = useMemo(() => {
        if (!periodSignoffsMeta?.startDate || !periodSignoffsMeta?.endDate) {
            return null;
        }
        return `${new Date(
            periodSignoffsMeta.startDate
        ).toLocaleDateString()} – ${new Date(
            periodSignoffsMeta.endDate
        ).toLocaleDateString()}`;
    }, [periodSignoffsMeta]);

    const signedOffTechLabelsForPeriod = useMemo(() => {
        if (!periodSignoffs?.length) return [];
        const map = new Map(
            userOptionsForSelect
                .filter((o) => o.value !== '' && o.value != null)
                .map((o) => [String(o.value), o.label])
        );
        return periodSignoffs.map((s) => {
            const id = String(s.techOracleUserId);
            return { id, label: map.get(id) || 'Technician' };
        });
    }, [periodSignoffs, userOptionsForSelect]);

    const selectedTechLabel = useMemo(
        () =>
            userOptionsForSelect.find((o) => o.value === formik.values.userId)
                ?.label || '',
        [userOptionsForSelect, formik.values.userId]
    );

    const signedOffNamesSentence = useMemo(() => {
        const labels = signedOffTechLabelsForPeriod.map((x) => x.label);
        if (!labels.length) return '';
        if (labels.length <= 3) return labels.join(', ');
        return `${labels.slice(0, 3).join(', ')} and ${labels.length - 3} more`;
    }, [signedOffTechLabelsForPeriod]);

    const signedTechIdSet = useMemo(() => {
        const s = new Set();
        (periodSignoffs || []).forEach((row) => {
            if (row.techOracleUserId != null) {
                s.add(String(row.techOracleUserId));
            }
        });
        return s;
    }, [periodSignoffs]);

    const teamPeriodSignoffProgress = useMemo(() => {
        if (
            !formik.values.teamHdName ||
            formik.values.teamHdName === 'Leadership'
        ) {
            return null;
        }
        if (!teamWideNoTechSelected) return null;
        if (periodSignoffs === null) return null;
        const total = Array.isArray(techOptions) ? techOptions.length : 0;
        if (total === 0) return null;
        const signed = signedTechIdSet.size;
        return {
            total,
            signed,
            pending: Math.max(0, total - signed)
        };
    }, [
        formik.values.teamHdName,
        teamWideNoTechSelected,
        periodSignoffs,
        techOptions,
        signedTechIdSet
    ]);

    const existingSignoffForSelectedTech = useMemo(() => {
        if (!formik.values.userId || !periodSignoffs?.length) return null;
        return (
            periodSignoffs.find(
                (r) =>
                    String(r.techOracleUserId) === String(formik.values.userId)
            ) || null
        );
    }, [formik.values.userId, periodSignoffs]);

    useEffect(() => {
        if (!Array.isArray(tickets) || tickets.length === 0) {
            setTechReviewFlags({});
            return;
        }
        const existingFlagged = new Set(
            (
                existingSignoffForSelectedTech?.reviewFlagTicketHumanIds || []
            ).map((id) => String(id).trim())
        );
        if (!existingFlagged.size) {
            setTechReviewFlags({});
            return;
        }
        const next = {};
        tickets.forEach((t) => {
            const hid = String(t.humanId || '').trim();
            const rowKey = String(t.humanId || t._id || '').trim();
            if (hid && rowKey && existingFlagged.has(hid)) {
                next[rowKey] = true;
            }
        });
        setTechReviewFlags(next);
    }, [tickets, existingSignoffForSelectedTech]);

    const formatTechOptionLabel = useCallback(
        (option) => {
            if (option.value === '' || option.value == null) {
                return option.label;
            }
            if (!signedTechIdSet.has(String(option.value))) {
                return option.label;
            }
            return (
                <span>
                    {option.label}{' '}
                    <span className="audit-weekly-tech-signed-suffix">
                        · Signed off
                    </span>
                </span>
            );
        },
        [signedTechIdSet]
    );

    let signoffSubmitButtonLabel = 'Submit sign-off';
    if (signLoading) {
        signoffSubmitButtonLabel = 'Saving…';
    } else if (existingSignoffForSelectedTech) {
        signoffSubmitButtonLabel = 'Update sign-off';
    }

    return (
        <div className="audit-weekly-page">
            <p className="audit-weekly-intro">
                Choose filters, then <strong>Start audit</strong>. Ticket IDs
                open in Pearl. The sidebar shows how many sign-offs you already
                have for the selected time range.
            </p>

            <div className="audit-weekly-layout">
                <div className="audit-weekly-panel audit-weekly-panel--form">
                    <form
                        className="audit-weekly-form"
                        onSubmit={formik.handleSubmit}>
                        <div className="audit-weekly-toolbar">
                            <div className="audit-weekly-field">
                                <label htmlFor="teamHdName">Team</label>
                                <Select
                                    id="teamHdName"
                                    name="teamHdName"
                                    inputId="teamHdName-input"
                                    options={teamSelectOptions}
                                    styles={selectStyles}
                                    isDisabled={teamSelectLocked}
                                    value={teamSelectOptions.find(
                                        (option) =>
                                            option.value ===
                                            formik.values.teamHdName
                                    )}
                                    onChange={(selectedOption) => {
                                        formik.setFieldValue(
                                            'teamHdName',
                                            selectedOption
                                                ? selectedOption.value
                                                : ''
                                        );
                                        formik.setFieldValue('userId', '');
                                    }}
                                />
                                {teamSelectLocked ? (
                                    <p className="audit-weekly-field-hint audit-weekly-field-hint--tight">
                                        Team is fixed to your HD team (team
                                        lead).
                                    </p>
                                ) : null}
                            </div>

                            <div className="audit-weekly-field">
                                <label htmlFor="userId">Technician</label>
                                <Select
                                    id="userId"
                                    name="userId"
                                    inputId="userId-input"
                                    options={userOptionsForSelect}
                                    styles={selectStyles}
                                    formatOptionLabel={formatTechOptionLabel}
                                    placeholder="All techs on team if empty"
                                    value={userOptionsForSelect.find(
                                        (option) =>
                                            option.value ===
                                            formik.values.userId
                                    )}
                                    onChange={handleTechnicianChange}
                                />
                                {periodSignoffsLoading && (
                                    <p className="audit-weekly-field-hint audit-weekly-field-hint--tight">
                                        Loading sign-off status…
                                    </p>
                                )}
                                {formik.values.userId &&
                                existingSignoffForSelectedTech ? (
                                    <p className="audit-weekly-field-hint">
                                        You have a sign-off for this technician
                                        in the selected period. Submitting
                                        replaces initials, notes, ticket IDs,
                                        and score snapshot (same as
                                        &quot;edit&quot;).
                                    </p>
                                ) : null}
                                {userLoading && <LoadingSpinner />}
                                {userError && (
                                    <ErrorMessage message={userError.message} />
                                )}
                            </div>

                            <div className="audit-weekly-field audit-weekly-field--inline">
                                <label htmlFor="ticketHumanId">Ticket ID</label>
                                <input
                                    id="ticketHumanId"
                                    name="ticketHumanId"
                                    type="text"
                                    className="audit-weekly-input"
                                    placeholder="Filter (optional)"
                                    value={formik.values.ticketHumanId}
                                    onChange={formik.handleChange}
                                />
                            </div>

                            <div className="audit-weekly-field">
                                <label htmlFor="auditStatus">Result</label>
                                <Select
                                    id="auditStatus"
                                    name="auditStatus"
                                    inputId="auditStatus-input"
                                    options={auditStatusOptions}
                                    styles={selectStyles}
                                    value={auditStatusOptions.find(
                                        (option) =>
                                            option.value ===
                                            formik.values.auditStatus
                                    )}
                                    onChange={(selectedOption) =>
                                        formik.setFieldValue(
                                            'auditStatus',
                                            selectedOption
                                                ? selectedOption.value
                                                : 'all'
                                        )
                                    }
                                />
                            </div>

                            <div className="audit-weekly-field">
                                <label htmlFor="ticketCount">Sample size</label>
                                <input
                                    id="ticketCount"
                                    name="ticketCount"
                                    type="number"
                                    min={1}
                                    max={50}
                                    className="audit-weekly-input audit-weekly-input--narrow"
                                    value={formik.values.ticketCount}
                                    onChange={formik.handleChange}
                                    disabled={
                                        formik.values.auditStatus === 'all'
                                    }
                                />
                                <p className="audit-weekly-field-hint">
                                    {ticketLimitHint}
                                </p>
                                {formik.errors.ticketCount ? (
                                    <ErrorMessage
                                        message={formik.errors.ticketCount}
                                    />
                                ) : null}
                            </div>

                            <div className="audit-weekly-field audit-weekly-field--completed-audits">
                                <label className="audit-weekly-checkbox audit-weekly-checkbox--block">
                                    <CheckBox
                                        checked={
                                            formik.values.completedAuditsOnly
                                        }
                                        onChange={() =>
                                            formik.setFieldValue(
                                                'completedAuditsOnly',
                                                !formik.values
                                                    .completedAuditsOnly
                                            )
                                        }
                                    />
                                    <span>Signed-off tickets only</span>
                                </label>
                                <p className="audit-weekly-field-hint audit-weekly-field-hint--tight">
                                    Other filters still apply.
                                </p>
                            </div>

                            <div className="audit-weekly-field">
                                <span className="audit-weekly-label">
                                    Time period
                                </span>
                                <div className="audit-weekly-time-row">
                                    {showCustomTime ? (
                                        <div className="audit-weekly-custom-dates">
                                            <div className="hipaa-date-container">
                                                <div
                                                    className="hipaa-date-chunk"
                                                    style={{ zIndex: 2 }}>
                                                    <p>Start</p>
                                                    <DatePicker
                                                        onChange={(date) =>
                                                            formik.setFieldValue(
                                                                'customStartDate',
                                                                date
                                                            )
                                                        }
                                                        selected={
                                                            formik.values
                                                                .customStartDate
                                                        }
                                                        maxDate={new Date()}
                                                        minDate={
                                                            new Date(
                                                                '2015-10-27'
                                                            )
                                                        }
                                                        isClearable
                                                        showYearDropdown
                                                        scrollableMonthYearDropdown
                                                    />
                                                </div>
                                                <div
                                                    className="hipaa-date-chunk"
                                                    style={{ zIndex: 1 }}>
                                                    <p>End</p>
                                                    <DatePicker
                                                        onChange={(date) =>
                                                            formik.setFieldValue(
                                                                'customEndDate',
                                                                date
                                                            )
                                                        }
                                                        selected={
                                                            formik.values
                                                                .customEndDate
                                                        }
                                                        maxDate={new Date()}
                                                        minDate={
                                                            new Date(
                                                                '2015-10-27'
                                                            )
                                                        }
                                                        isClearable
                                                        showYearDropdown
                                                        scrollableMonthYearDropdown
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="audit-weekly-select-grow">
                                            <Select
                                                id="timePeriod"
                                                name="timePeriod"
                                                inputId="timePeriod-input"
                                                options={timePeriodOptions}
                                                styles={selectStyles}
                                                value={timePeriodOptions.find(
                                                    (option) =>
                                                        option.value ===
                                                        formik.values.timePeriod
                                                )}
                                                onChange={(selectedOption) =>
                                                    formik.setFieldValue(
                                                        'timePeriod',
                                                        selectedOption
                                                            ? selectedOption.value
                                                            : null
                                                    )
                                                }
                                            />
                                        </div>
                                    )}
                                    <label className="audit-weekly-checkbox">
                                        <CheckBox
                                            checked={showCustomTime}
                                            onChange={() =>
                                                setShowCustomTime(
                                                    !showCustomTime
                                                )
                                            }
                                        />
                                        <p>Custom range</p>
                                    </label>
                                </div>
                                {formik.touched.timePeriod &&
                                formik.errors.timePeriod ? (
                                    <ErrorMessage
                                        message={formik.errors.timePeriod}
                                    />
                                ) : null}
                            </div>

                            {!periodSignoffsLoading &&
                            periodSignoffs &&
                            periodSignoffs.length > 0 &&
                            filterPeriodLabelDisplay ? (
                                <div
                                    className="audit-weekly-sidebar-signoff-summary"
                                    role="status">
                                    <strong>
                                        {periodSignoffs.length} sign-off
                                        {periodSignoffs.length === 1
                                            ? ''
                                            : 's'}{' '}
                                        on file
                                    </strong>
                                    <span className="audit-weekly-sidebar-signoff-summary__window">
                                        {filterPeriodLabelDisplay}
                                    </span>
                                    {signedOffNamesSentence ? (
                                        <p className="audit-weekly-sidebar-signoff-summary__names">
                                            {signedOffNamesSentence}
                                        </p>
                                    ) : null}
                                    <p className="audit-weekly-sidebar-signoff-summary__hint">
                                        In the technician menu, completed rows
                                        show{' '}
                                        <span className="audit-weekly-tech-signed-suffix audit-weekly-tech-signed-suffix--inline">
                                            · Signed off
                                        </span>
                                        .
                                    </p>
                                </div>
                            ) : null}

                            {!periodSignoffsLoading &&
                            teamPeriodSignoffProgress &&
                            filterPeriodLabelDisplay ? (
                                <div
                                    className="audit-weekly-sidebar-team-progress"
                                    role="status">
                                    <strong>Complete vs pending</strong>
                                    <span className="audit-weekly-sidebar-team-progress__line">
                                        {teamPeriodSignoffProgress.signed} of{' '}
                                        {teamPeriodSignoffProgress.total}{' '}
                                        technicians signed off for{' '}
                                        {filterPeriodLabelDisplay}.
                                    </span>
                                    <span className="audit-weekly-sidebar-team-progress__pending">
                                        {teamPeriodSignoffProgress.pending}{' '}
                                        pending.
                                    </span>
                                </div>
                            ) : null}

                            {formik.submitCount > 0 &&
                                formik.errors.teamHdName && (
                                    <ErrorMessage
                                        message={formik.errors.teamHdName}
                                    />
                                )}

                            {formik.submitCount > 0 &&
                                Object.keys(formik.errors).length > 0 &&
                                !formik.errors.teamHdName && (
                                    <ErrorMessage message="There are form errors above, please fix." />
                                )}

                            <div className="audit-weekly-actions audit-weekly-actions--toolbar">
                                <SubmitButton label="Start audit" />
                            </div>
                        </div>
                    </form>
                </div>

                <div className="audit-weekly-panel audit-weekly-panel--results">
                    {formik.values.userId &&
                    existingSignoffForSelectedTech &&
                    periodSignoffs !== null &&
                    !periodSignoffsLoading ? (
                        <div
                            className="audit-weekly-prior-signoff-banner"
                            role="status">
                            <div className="audit-weekly-prior-signoff-banner__title">
                                Signed off for this period
                            </div>
                            <p className="audit-weekly-prior-signoff-banner__meta">
                                {selectedTechLabel ? (
                                    <strong>{selectedTechLabel}</strong>
                                ) : null}
                                {filterPeriodLabelDisplay ? (
                                    <>
                                        {selectedTechLabel ? ' · ' : null}
                                        {filterPeriodLabelDisplay}
                                    </>
                                ) : null}
                                {existingSignoffForSelectedTech.initials ? (
                                    <>
                                        {' · '}
                                        {
                                            existingSignoffForSelectedTech.initials
                                        }
                                    </>
                                ) : null}
                                {existingSignoffForSelectedTech.leadValidatedAt ? (
                                    <>
                                        {' · '}
                                        validated{' '}
                                        {new Date(
                                            existingSignoffForSelectedTech.leadValidatedAt
                                        ).toLocaleDateString()}
                                    </>
                                ) : null}
                            </p>
                            <p className="audit-weekly-prior-signoff-banner__hint">
                                Run <strong>Start audit</strong> to load tickets
                                again; you can update this sign-off afterward.
                            </p>
                        </div>
                    ) : null}

                    {reportingLoading && <LoadingSpinner />}
                    {reportingError && (
                        <ErrorMessage message={reportingError.message} />
                    )}

                    {!reportingLoading && showTicketResults && periodLabel && (
                        <p className="audit-weekly-period">
                            <strong>Audit window:</strong> {periodLabel}
                        </p>
                    )}

                    {!reportingLoading && showTicketResults && (
                        <>
                            <div className="audit-weekly-results-scroll">
                                <div
                                    className={`audit-weekly-table-wrap audit-weekly-table-wrap--${
                                        showTechOnCards
                                            ? 'with-tech'
                                            : 'no-tech'
                                    }`}>
                                    <div
                                        className="audit-ticket-table"
                                        role="table"
                                        aria-label="Weekly audit tickets">
                                        <div
                                            className="audit-ticket-row audit-ticket-row--head"
                                            role="row">
                                            {showTechOnCards ? (
                                                <div
                                                    role="columnheader"
                                                    className="audit-ticket-row__cell audit-ticket-row__tech">
                                                    Tech
                                                </div>
                                            ) : null}
                                            <div
                                                role="columnheader"
                                                className="audit-ticket-row__cell audit-ticket-row__id">
                                                Ticket ID
                                            </div>
                                            <div
                                                role="columnheader"
                                                className="audit-ticket-row__cell audit-ticket-row__summary">
                                                Summary
                                            </div>
                                            <div
                                                role="columnheader"
                                                className="audit-ticket-row__cell audit-ticket-row__passfail">
                                                Audit
                                            </div>
                                            <div
                                                role="columnheader"
                                                className="audit-ticket-row__cell audit-ticket-row__sla">
                                                SLA / flags
                                            </div>
                                            <div
                                                role="columnheader"
                                                className="audit-ticket-row__cell audit-ticket-row__pearl">
                                                Pearl
                                            </div>
                                            <div
                                                role="columnheader"
                                                className="audit-ticket-row__cell audit-ticket-row__review">
                                                Review
                                            </div>
                                        </div>
                                        {tickets.map((ticket) => {
                                            const tkey = String(
                                                ticket.humanId ||
                                                    ticket._id ||
                                                    ''
                                            ).trim();
                                            return (
                                                <TicketCard
                                                    key={
                                                        ticket._id ||
                                                        ticket.humanId
                                                    }
                                                    ticket={ticket}
                                                    showTechColumn={
                                                        showTechOnCards
                                                    }
                                                    flaggedForTechReview={
                                                        !!techReviewFlags[tkey]
                                                    }
                                                    onFlaggedForTechReviewChange={
                                                        onTechReviewFlagChange
                                                    }
                                                />
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>

                            <div className="audit-weekly-signoff">
                                <h3>Sign-off &amp; public score</h3>
                                {signoffSavedSummary ? (
                                    <div
                                        className="audit-weekly-signoff-success"
                                        role="status">
                                        <div className="audit-weekly-signoff-success__title">
                                            Sign-off recorded
                                        </div>
                                        <p className="audit-weekly-signoff-success__meta">
                                            {signoffSavedSummary.techName ? (
                                                <span>
                                                    {
                                                        signoffSavedSummary.techName
                                                    }
                                                    {' · '}
                                                </span>
                                            ) : null}
                                            {signoffSavedSummary.periodLabel ? (
                                                <span>
                                                    {
                                                        signoffSavedSummary.periodLabel
                                                    }
                                                    {' · '}
                                                </span>
                                            ) : null}
                                            {signoffSavedSummary.ticketCount}{' '}
                                            ticket
                                            {signoffSavedSummary.ticketCount ===
                                            1
                                                ? ''
                                                : 's'}
                                            {signoffSavedSummary.reviewFlagCount
                                                ? ` · ${signoffSavedSummary.reviewFlagCount} flagged for review`
                                                : ''}
                                            {signoffSavedSummary.initials ? (
                                                <span>
                                                    {' '}
                                                    ·{' '}
                                                    {
                                                        signoffSavedSummary.initials
                                                    }
                                                </span>
                                            ) : null}
                                        </p>
                                        <button
                                            type="button"
                                            className="audit-weekly-signoff-success__dismiss"
                                            onClick={() =>
                                                setSignoffSavedSummary(null)
                                            }>
                                            Dismiss
                                        </button>
                                    </div>
                                ) : null}
                                <p className="audit-weekly-signoff-desc">
                                    Initials and date record this run. The
                                    public score uses the table above (any
                                    automated fail flag). Re-submitting for the
                                    same week and technician updates the stored
                                    sign-off.
                                </p>
                                {publicScore &&
                                typeof publicScore.closedTotal === 'number' ? (
                                    <div
                                        className="audit-weekly-public-score audit-weekly-public-score--in-signoff"
                                        aria-label="Public score">
                                        <strong>
                                            {formik.values.auditStatus === 'all'
                                                ? 'Public score (all closed tickets)'
                                                : 'Public score (this sample)'}
                                        </strong>
                                        <div className="audit-weekly-public-score__list">
                                            <div>
                                                Total tickets:{' '}
                                                <strong>
                                                    {publicScore.closedTotal}
                                                </strong>
                                            </div>
                                            <div>
                                                Failed (any audit flag):{' '}
                                                <strong>
                                                    {publicScore.failedPct !=
                                                    null
                                                        ? `${publicScore.failedPct}%`
                                                        : '—'}
                                                </strong>
                                                {publicScore.failedCount !=
                                                null ? (
                                                    <span className="audit-weekly-public-score__sub">
                                                        {' '}
                                                        (
                                                        {
                                                            publicScore.failedCount
                                                        }{' '}
                                                        of{' '}
                                                        {
                                                            publicScore.closedTotal
                                                        }
                                                        )
                                                    </span>
                                                ) : null}
                                            </div>
                                        </div>
                                    </div>
                                ) : null}
                                <label htmlFor="audit-lead-date">
                                    Validation date
                                </label>
                                <input
                                    id="audit-lead-date"
                                    type="date"
                                    className="audit-weekly-input"
                                    value={signLeadDate}
                                    onChange={(e) =>
                                        setSignLeadDate(e.target.value)
                                    }
                                />
                                <label htmlFor="audit-initials">
                                    Initials (required)
                                </label>
                                <input
                                    id="audit-initials"
                                    autoComplete="name"
                                    value={signInitials}
                                    onChange={(e) =>
                                        setSignInitials(e.target.value)
                                    }
                                />
                                <label
                                    htmlFor="audit-feedback"
                                    className="audit-weekly-signoff-label--spaced">
                                    Feedback to tech (optional)
                                </label>
                                <textarea
                                    id="audit-feedback"
                                    value={signFeedback}
                                    onChange={(e) =>
                                        setSignFeedback(e.target.value)
                                    }
                                    rows={3}
                                />
                                <div className="audit-weekly-signoff-footer">
                                    <button
                                        type="button"
                                        className="newsc-btn"
                                        disabled={signLoading}
                                        onClick={submitSignoff}>
                                        {signoffSubmitButtonLabel}
                                    </button>
                                    {signMessage ? (
                                        <p className={signMessageClass}>
                                            {signMessage}
                                        </p>
                                    ) : null}
                                </div>
                            </div>

                            <div className="audit-weekly-reset-row">
                                <button
                                    type="button"
                                    className="newsc-btn"
                                    onClick={handleClearAuditResults}>
                                    Clear results
                                </button>
                            </div>
                        </>
                    )}

                    {!reportingLoading &&
                        auditContext?.mode === 'leadership' &&
                        auditContext?.leadershipMessage && (
                            <div className="audit-weekly-empty audit-weekly-empty--leadership">
                                <h3>Leadership (team: Leadership)</h3>
                                <p>{auditContext.leadershipMessage}</p>
                                <p className="audit-weekly-empty--leadership-hint">
                                    Use <strong>Monthly sign-off rollup</strong>{' '}
                                    at the bottom of this column for org-wide
                                    counts. Select a technician to run a
                                    ticket-level audit.
                                </p>
                            </div>
                        )}

                    {!reportingLoading && hasRunWithNoTickets && (
                        <>
                            <div className="audit-weekly-empty">
                                <h3>No tickets in this window</h3>
                                <p>
                                    Try another team or technician, widen the
                                    date range, adjust pass/fail filters, or
                                    turn off &quot;Signed-off tickets only&quot;
                                    if no sign-offs exist yet for this period.
                                </p>
                            </div>
                            <div className="audit-weekly-reset-row">
                                <button
                                    type="button"
                                    className="newsc-btn"
                                    onClick={handleClearAuditResults}>
                                    Clear results
                                </button>
                            </div>
                        </>
                    )}

                    {!reportingLoading &&
                        tickets === null &&
                        !reportingError &&
                        (formik.values.userId &&
                        existingSignoffForSelectedTech &&
                        periodSignoffs !== null &&
                        !periodSignoffsLoading ? (
                            <div className="audit-weekly-empty audit-weekly-empty--prior-signoff">
                                <h3>Already signed off for this week</h3>
                                <p>
                                    {selectedTechLabel ? (
                                        <>
                                            <strong>{selectedTechLabel}</strong>{' '}
                                        </>
                                    ) : null}
                                    has a completed sign-off for{' '}
                                    {filterPeriodLabelDisplay ||
                                        'the selected period'}
                                    .{' Run '}
                                    <strong>Start audit</strong> to pull tickets
                                    and review or update the record.
                                </p>
                            </div>
                        ) : (
                            <div className="audit-weekly-empty">
                                <h3>Ready to audit</h3>
                                <p>
                                    Choose filters and run the audit. Results
                                    appear here with Pearl links on each ticket
                                    ID for follow-up.
                                </p>
                            </div>
                        ))}

                    {isLeadership ? (
                        <details className="audit-weekly-management-details">
                            <summary className="audit-weekly-management-details__summary">
                                Monthly sign-off rollup
                            </summary>
                            <p className="audit-weekly-management-details__desc">
                                Per-technician counts, sign-off volume by audit
                                week, and sample ticket IDs for a calendar month
                                (leadership).
                            </p>
                            <div className="audit-weekly-management__row">
                                <label htmlFor="mgmt-month">Month</label>
                                <input
                                    id="mgmt-month"
                                    type="month"
                                    className="audit-weekly-input audit-weekly-input--month"
                                    value={mgmtMonth}
                                    onChange={(e) =>
                                        setMgmtMonth(e.target.value)
                                    }
                                />
                                <button
                                    type="button"
                                    className="newsc-btn"
                                    disabled={mgmtLoading}
                                    onClick={loadManagementSummary}>
                                    {mgmtLoading ? 'Loading…' : 'Load summary'}
                                </button>
                            </div>
                            {mgmtData && (
                                <div className="audit-weekly-management__table-wrap">
                                    <p className="audit-weekly-management__meta">
                                        <strong>
                                            {mgmtData.completeCount}
                                        </strong>{' '}
                                        sign-off(s) in {mgmtData.month}.
                                    </p>
                                    {Array.isArray(mgmtData.weekBreakdown) &&
                                    mgmtData.weekBreakdown.length > 0 ? (
                                        <div className="audit-weekly-management__week-wrap">
                                            <p className="audit-weekly-management__week-title">
                                                By audit week
                                            </p>
                                            <table className="audit-weekly-management__table audit-weekly-management__table--compact">
                                                <thead>
                                                    <tr>
                                                        <th>Week</th>
                                                        <th>Sign-offs</th>
                                                        <th>Distinct techs</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {mgmtData.weekBreakdown.map(
                                                        (w, idx) => (
                                                            <tr
                                                                key={`${w.weekStart}-${idx}`}>
                                                                <td>
                                                                    {w.weekStart &&
                                                                    w.weekEnd
                                                                        ? `${new Date(
                                                                              w.weekStart
                                                                          ).toLocaleDateString()} – ${new Date(
                                                                              w.weekEnd
                                                                          ).toLocaleDateString()}`
                                                                        : '—'}
                                                                </td>
                                                                <td>
                                                                    {
                                                                        w.signoffRowCount
                                                                    }
                                                                </td>
                                                                <td>
                                                                    {
                                                                        w.distinctTechCount
                                                                    }
                                                                </td>
                                                            </tr>
                                                        )
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : null}
                                    <table className="audit-weekly-management__table">
                                        <thead>
                                            <tr>
                                                <th>Technician</th>
                                                <th>Reviews</th>
                                                <th>Last sign-off</th>
                                                <th>Sample ticket IDs</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(mgmtData.monthlyByTech || []).map(
                                                (row) => (
                                                    <tr
                                                        key={
                                                            row.techOracleUserId
                                                        }>
                                                        <td>{row.techName}</td>
                                                        <td>
                                                            {row.reviewedCount}
                                                        </td>
                                                        <td>
                                                            {row.lastSignoffAt
                                                                ? new Date(
                                                                      row.lastSignoffAt
                                                                  ).toLocaleString()
                                                                : '—'}
                                                            {row.lastInitials
                                                                ? ` (${row.lastInitials})`
                                                                : ''}
                                                        </td>
                                                        <td className="audit-weekly-management__samples">
                                                            {(
                                                                row.sampleTicketHumanIds ||
                                                                []
                                                            )
                                                                .filter(Boolean)
                                                                .join(', ') ||
                                                                '—'}
                                                        </td>
                                                    </tr>
                                                )
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </details>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

AuditTicketsForm.propTypes = {
    isLeadership: PropTypes.bool,
    isTeamLead: PropTypes.bool,
    hdTeam: PropTypes.string
};

export default AuditTicketsForm;
